import { useMemo } from 'react';
import {
  isONAEnabled,
  isRatingDisabledForCampaign,
} from 'utils/models/Performance';
import {
  getCampaignHasFeatureEnabled,
  isWizardType,
  PERFORMANCE_FEATURE_ALLOW_EXTERNAL_MANAGER_RATINGS,
  PERFORMANCE_FEATURE_ASSESS_MANAGER,
  PERFORMANCE_FEATURE_ASSESS_ORGANIZATION,
  PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED,
  PERFORMANCE_FEATURE_HIGH_PRIORITY_PEERS,
  PERFORMANCE_FEATURE_PEER_FEEDBACK,
  PHASE_TYPE_EVALUATION,
  PHASE_TYPE_SELF,
  WIZARD_TYPE_PERFORMANCE,
} from 'utils/models/PerformanceUtils';
import { Campaign } from '../../../types';
import { getPhaseByType } from '../../../utils/models/Campaign';

export type CampaignToggles = {
  selfPhaseIsEnabled: boolean;
  onaIsEnabled: boolean;
  peer360sAreEnabled: boolean;
  upwardFeedbackisEnabled: boolean;
  evaluationPhaseIsEnabled: boolean;
  managerRatingsOfDirectsIsEnabled: boolean;
  highPriorityPeersAreEnabled: boolean;
  externalRatingsAreEnabled: boolean;
  engagementSurveyIsEnabled: boolean;
  npsIsEnabled: boolean;
};
export const useCampaignToggles = (campaign: Campaign): CampaignToggles => {
  const onaIsEnabled = useMemo(() => isONAEnabled(campaign), [campaign]);

  const selfPhaseIsEnabled = useMemo(
    () => !!getPhaseByType(campaign, PHASE_TYPE_SELF),
    [campaign]
  );

  const peer360sAreEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_PEER_FEEDBACK
      ) && isWizardType(campaign, WIZARD_TYPE_PERFORMANCE),
    [campaign]
  );

  const upwardFeedbackisEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ASSESS_MANAGER
      ) && isWizardType(campaign, WIZARD_TYPE_PERFORMANCE),
    [campaign]
  );

  const engagementSurveyIsEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ASSESS_ORGANIZATION
      ),
    [campaign]
  );

  const npsIsEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED
      ),
    [campaign]
  );

  const evaluationPhaseIsEnabled = useMemo(
    () => getPhaseByType(campaign, PHASE_TYPE_EVALUATION),
    [campaign]
  );

  const managerRatingsOfDirectsIsEnabled = useMemo(
    () => evaluationPhaseIsEnabled && !isRatingDisabledForCampaign(campaign),
    [evaluationPhaseIsEnabled, campaign]
  );

  const highPriorityPeersAreEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_HIGH_PRIORITY_PEERS
      ) && isWizardType(campaign, WIZARD_TYPE_PERFORMANCE),
    [campaign]
  );

  const externalRatingsAreEnabled = useMemo(
    () =>
      getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ALLOW_EXTERNAL_MANAGER_RATINGS
      ) && isWizardType(campaign, WIZARD_TYPE_PERFORMANCE),
    [campaign]
  );

  return {
    selfPhaseIsEnabled,
    onaIsEnabled,
    peer360sAreEnabled,
    upwardFeedbackisEnabled,
    evaluationPhaseIsEnabled,
    managerRatingsOfDirectsIsEnabled,
    highPriorityPeersAreEnabled,
    externalRatingsAreEnabled,
    engagementSurveyIsEnabled,
    npsIsEnabled,
  };
};
